import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useRef } from 'react'
import { ArrowRight, ArrowUpRight } from '@phosphor-icons/react'
import Footer from '../components/Footer'
import Navbar from '../components/Navbar'
import { useInquiry } from '../components/InquirySheet'
import { SiteButton, SiteButtonLink } from '../components/SiteButton'
import { usePageTitle } from '../hooks/usePageTitle'
import './AboutPage.css'

const TEAM = [
  {
    name: 'Hinata Yamazaki',
    role: 'Co-founder, CEO',
    image: '/founder-hinata.png',
  },
  {
    name: 'Haruhiro Matsumoto',
    role: 'Co-founder, CTO & CPO',
    image: '/founder-haruhiro.png',
  },
]

const SHOW_TEAM_SECTION = true

export default function AboutPage() {
  const { t } = useLocale()
  const { openInquiry } = useInquiry()
  usePageTitle(t("About"))
  const heroMediaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.classList.add('body-white', 'body-about')
    return () => document.body.classList.remove('body-white', 'body-about')
  }, [])

  useEffect(() => {
    const media = heroMediaRef.current
    if (!media) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let animationFrame = 0

    const updateParallax = () => {
      animationFrame = 0

      if (reduceMotion.matches) {
        media.style.setProperty('--about-parallax-y', '0px')
        return
      }

      const rect = media.getBoundingClientRect()
      const distanceFromCenter = (window.innerHeight / 2) - (rect.top + rect.height / 2)
      const maxOffset = rect.height * 0.1
      const offset = Math.max(-maxOffset, Math.min(maxOffset, distanceFromCenter * 0.14))

      media.style.setProperty('--about-parallax-y', `${offset.toFixed(2)}px`)
    }

    const requestParallaxUpdate = () => {
      if (animationFrame === 0) animationFrame = window.requestAnimationFrame(updateParallax)
    }

    const resizeObserver = new ResizeObserver(requestParallaxUpdate)
    resizeObserver.observe(media)
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true })
    window.addEventListener('resize', requestParallaxUpdate)
    reduceMotion.addEventListener('change', requestParallaxUpdate)
    updateParallax()

    return () => {
      if (animationFrame !== 0) window.cancelAnimationFrame(animationFrame)
      resizeObserver.disconnect()
      window.removeEventListener('scroll', requestParallaxUpdate)
      window.removeEventListener('resize', requestParallaxUpdate)
      reduceMotion.removeEventListener('change', requestParallaxUpdate)
      media.style.removeProperty('--about-parallax-y')
    }
  }, [])

  return (
    <>
      <Navbar lightMode />

      <main className="about-page">
        <header className="about-hero about-frame">
          <div className="about-inner">
            <div className="about-hero-copy">
              <h1>{t("Your Japan team should understand what you are building.")}</h1>
              <p> {t("Great products should not be stopped by language, culture, or distance. We help ambitious software teams enter Japan with local context and hands-on execution.")} </p>
            </div>

            <div className="about-hero-media" ref={heroMediaRef}>
              <img
                src="/about-tokyo-tower.jpg"
                alt={t("Tokyo Tower rising above the city skyline and surrounding greenery")}
              />
            </div>
          </div>
        </header>

        <section className="about-story about-frame" aria-labelledby="about-story-title">
          <div className="about-inner about-split">
            <div className="about-story-heading">
              <h2 id="about-story-title">{t("Japan’s window is open.")}</h2>
            </div>

            <div className="about-story-body">
              <p className="about-story-lead"> {t("Japan holds boundless opportunity. As a country still catching up in AI, and one where tightly-knit, often closed business communities have historically limited outside entry, there remains substantial room for new products and technologies to break into the market.")} </p>
              <p> {t("No one knows how long this window will stay open. So why let the opportunity slip by? At Bybeit, we help maximize the value of your product in the Japanese market — communicating what makes it exceptional, and empowering the people who will sell it.")} </p>
              <p> {t("If you have even the slightest interest in the Japanese market, or want to significantly expand your company's reach, let us help you get there.")} </p>
            </div>
          </div>
        </section>

        {SHOW_TEAM_SECTION && (
          <section className="about-team about-frame" aria-labelledby="about-team-title">
            <div className="about-inner">
              <div className="about-team-intro about-split">
                <div className="about-team-copy">
                  <h2 id="about-team-title">{t("Meet the team behind Bybeit.")}</h2>
                </div>
                <div className="about-team-note">
                  <p> {t("We are builders, operators, and problem solvers working across two markets. Every engagement stays connected to the people responsible for the outcome.")} </p>
                  <SiteButton variant="dark" size="small" onClick={() => openInquiry()}> {t("Work with us")} <ArrowUpRight size={15} weight="bold" />
                  </SiteButton>
                </div>
              </div>

              <div className="about-team-grid">
                {TEAM.map((member) => (
                  <article className="about-team-card" key={member.name}>
                    <img
                      className="about-founder-image"
                      src={member.image}
                      alt={`${t(member.name)}, ${t(member.role)}`}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="about-team-meta">
                      <div>
                        <h3>{t(member.name)}</h3>
                        <p>{t(member.role)}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="about-closing about-frame" aria-labelledby="about-closing-title">
          <div className="about-inner about-closing-inner">
            <h2 id="about-closing-title">{t('Markets, bridged.')}</h2>
            <p>{t("Tell us what you are building and where Japan fits into the story.")}</p>
            <div className="about-closing-actions">
              <SiteButton variant="dark" size="small" onClick={() => openInquiry()}> {t("Book a call")} <ArrowRight size={16} weight="bold" />
              </SiteButton>
              <SiteButtonLink to="/#services" variant="light" size="small"> {t("Explore services")} </SiteButtonLink>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
